import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * ROW SELECTION for the Command Center — one selection, shared by every view.
 *
 * Three properties, because an export that lies is worse than no export:
 *
 *  1. IT IS KEYED BY RECORD ID, not by row position. Sorting a column, paging a
 *     group, or switching from the Worklist to Gaps never moves a tick mark:
 *     the same record stays checked wherever it is drawn.
 *  2. IT RECONCILES AGAINST THE VISIBLE UNIVERSE. `items` is the CURRENT
 *     filtered set. The moment a filter drops a record, its id is pruned — so
 *     "Export selected" can never ship a row the operator can no longer see.
 *     (Records arriving from later pages are additive and never prune.)
 *  3. THE COUNT IS DERIVED, NOT CACHED. `selected` is recomputed from `items`,
 *     so "N selected" always equals the rows that would actually be written.
 *
 * Pure state — no fetch, no write, no backend surface.
 */
export default function useRowSelection(items) {
  const [ids, setIds] = useState(() => new Set())

  // The id set the selection is allowed to contain right now.
  const visible = useMemo(() => {
    const s = new Set()
    for (const c of items) if (c && c.id != null) s.add(String(c.id))
    return s
  }, [items])

  // Prune anything the active filters have taken off screen. Returning `prev`
  // untouched when nothing was dropped keeps this from looping.
  useEffect(() => {
    setIds((prev) => {
      if (!prev.size) return prev
      let stale = false
      for (const id of prev) if (!visible.has(id)) { stale = true; break }
      if (!stale) return prev
      const next = new Set()
      for (const id of prev) if (visible.has(id)) next.add(id)
      return next
    })
  }, [visible])

  const isSelected = useCallback((id) => ids.has(String(id)), [ids])

  const toggle = useCallback((id) => {
    const key = String(id)
    setIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  /** Tick (`on`) or untick every record in `list` — the header select-all. */
  const setMany = useCallback((list, on) => {
    setIds((prev) => {
      const next = new Set(prev)
      for (const c of list) {
        if (!c || c.id == null) continue
        if (on) next.add(String(c.id))
        else next.delete(String(c.id))
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => setIds(new Set(visible)), [visible])
  const clear = useCallback(() => setIds(new Set()), [])

  // Derived from `items`, so the order matches the board and the count is honest.
  const selected = useMemo(
    () => (ids.size ? items.filter((c) => c && c.id != null && ids.has(String(c.id))) : []),
    [items, ids]
  )

  /** How many of `list` are ticked — drives the header's tri-state. */
  const countIn = useCallback((list) => {
    if (!ids.size) return 0
    let n = 0
    for (const c of list) if (c && c.id != null && ids.has(String(c.id))) n += 1
    return n
  }, [ids])

  return { selected, count: selected.length, isSelected, toggle, setMany, selectAll, clear, countIn }
}
